import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Plus,
  Pencil,
  Archive,
  Undo2,
  FileText,
  Mic,
  MessageSquare,
  Sparkles,
  Brain,
  User,
} from 'lucide-react'
import type { ContextChange } from '@/types/contextEngine'
import { DOMAIN_LABELS } from '@/types/contextEngine'
import { DOMAIN_ICONS } from '@/components/context/domainIcons'
import { ContextDiffView } from './ContextDiffView'

// ─── Change type display ─────────────────────────────────────────────────────

const CHANGE_TYPE_CONFIG: Record<
  ContextChange['changeType'],
  { icon: typeof Plus; label: string; color: string }
> = {
  created: { icon: Plus, label: 'Created', color: 'text-accent-success' },
  updated: { icon: Pencil, label: 'Updated', color: 'text-accent-amber' },
  archived: { icon: Archive, label: 'Archived', color: 'text-text-tertiary' },
  reverted: { icon: Undo2, label: 'Reverted', color: 'text-blue-400' },
}

const SOURCE_ICONS: Record<ContextChange['sourceType'], typeof FileText> = {
  document: FileText,
  audio: Mic,
  interview: MessageSquare,
  artifact_feedback: Sparkles,
  manual_edit: User,
  inference: Brain,
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface HistoryTimelineProps {
  changes: ContextChange[]
  onRevert: (changeId: string) => void
}

export function HistoryTimeline({ changes, onRevert }: HistoryTimelineProps) {
  // Group by date
  const grouped = groupByDate(changes)

  return (
    <div className="space-y-6">
      {grouped.map(([dateLabel, items]) => (
        <div key={dateLabel}>
          <h3 className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-text-tertiary">
            {dateLabel}
          </h3>
          <div className="relative space-y-1 pl-6 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-border">
            {items.map((change) => (
              <ChangeEntry key={change.changeId} change={change} onRevert={onRevert} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Change entry ────────────────────────────────────────────────────────────

function ChangeEntry({ change, onRevert }: { change: ContextChange; onRevert: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const DomainIcon = DOMAIN_ICONS[change.domain]
  const changeConfig = CHANGE_TYPE_CONFIG[change.changeType]
  const ChangeIcon = changeConfig.icon
  const SourceIcon = SOURCE_ICONS[change.sourceType]
  const hasDiff = Boolean(change.oldText || change.newText)
  const canRevert = change.changeType === 'updated' || change.changeType === 'created'

  const timeLabel = new Date(change.timestamp).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })

  const handleToggle = useCallback(() => {
    if (hasDiff) setExpanded((v) => !v)
  }, [hasDiff])

  return (
    <div className="group relative">
      {/* Timeline dot */}
      <div className={`absolute -left-6 top-2.5 h-[10px] w-[10px] rounded-full border-2 border-bg-primary ${changeConfig.color} bg-current`} />

      <button
        onClick={handleToggle}
        disabled={!hasDiff}
        className={`flex w-full items-start gap-3 rounded-lg p-3 text-left transition-colors ${
          hasDiff ? 'hover:bg-bg-surface cursor-pointer' : 'cursor-default'
        }`}
      >
        {/* Domain icon */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-bg-elevated">
          <DomainIcon size={14} className="text-text-secondary" />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <ChangeIcon size={12} className={changeConfig.color} />
            <span className={`text-xs font-medium ${changeConfig.color}`}>
              {changeConfig.label}
            </span>
            <span className="text-xs text-text-secondary">
              {DOMAIN_LABELS[change.domain]}
            </span>
            <span className="ml-auto flex items-center gap-1 text-xs text-text-tertiary">
              <SourceIcon size={11} />
              {timeLabel}
            </span>
          </div>

          {/* Preview text */}
          <p className="mt-1 text-sm text-text-secondary line-clamp-1">
            {change.newText ?? change.oldText ?? ''}
          </p>
        </div>

        {/* Expand indicator */}
        {hasDiff && (
          <span className="mt-1 text-text-tertiary">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
      </button>

      {/* Expandable diff */}
      <AnimatePresence>
        {expanded && hasDiff && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="ml-11 mr-3 mb-2"
          >
            <ContextDiffView oldText={change.oldText} newText={change.newText} />
            {canRevert && (
              <div className="mt-2 flex justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (window.confirm('Revert this change? The chunk will be restored to its previous version.')) {
                      onRevert(change.changeId)
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-bg-elevated px-3 py-1.5 text-xs text-text-secondary transition-colors hover:bg-bg-surface-hover hover:text-text-primary"
                >
                  <RotateCcw size={12} />
                  Revert
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupByDate(changes: ContextChange[]): [string, ContextChange[]][] {
  const groups = new Map<string, ContextChange[]>()
  const today = new Date().toDateString()
  const yesterday = new Date(Date.now() - 86400000).toDateString()

  for (const change of changes) {
    const dateStr = new Date(change.timestamp).toDateString()
    let label: string
    if (dateStr === today) label = 'Today'
    else if (dateStr === yesterday) label = 'Yesterday'
    else label = new Date(change.timestamp).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })

    const group = groups.get(label) ?? []
    group.push(change)
    groups.set(label, group)
  }

  return Array.from(groups.entries())
}
